<template>
  <svg
    ref="svg"
    class="spinner-morph-c"
    viewBox="0 0 120 134"
    role="img"
    aria-label="Loading"
  />
</template>

<script>
const TAU = Math.PI * 2;
const SPINNER_SLOTS = 14;
const SVG_NS = 'http://www.w3.org/2000/svg';
const FILL_COLOR = '#ec9148';

const C_DOTS = [[88.62,89.25,0,0,18],[82.06,94.21,0,1,18],[74.53,97.39,0,2,18],[66.37,98.47,0,3,18],[58.14,97.39,0,4,18],[50.7,94.27,0,5,18],[44.07,89.3,0,6,18],[39.08,82.75,0,7,18],[35.92,75.28,0,8,18],[34.85,67.02,0,9,18],[35.85,58.96,0,10,18],[39.04,51.35,0,11,18],[44.03,44.84,0,12,18],[50.53,39.85,0,13,18],[58.1,36.66,0,14,18],[66.33,35.53,0,15,18],[74.32,36.59,0,16,18],[81.98,39.69,0,17,18],[74.72,106.33,1,0,18],[66.37,107.16,1,1,18],[58.0,106.39,1,2,18],[50.02,103.85,1,3,18],[42.73,99.62,1,4,18],[36.46,94.01,1,5,18],[31.54,87.2,1,6,18],[28.14,79.48,1,7,18],[26.37,71.31,1,8,18],[26.32,62.84,1,9,18],[28.04,54.6,1,10,18],[31.5,46.96,1,11,18],[36.38,40.13,1,12,18],[42.62,34.57,1,13,18],[49.81,30.29,1,14,18],[57.95,27.75,1,15,18],[66.28,26.83,1,16,18],[88.5,44.68,1,17,18],[96.18,93.95,2,0,17],[89.93,99.53,2,1,17],[82.7,103.73,2,2,17],[34.69,104.74,2,3,17],[28.6,98.71,2,4,17],[23.7,91.72,2,5,17],[20.08,83.91,2,6,17],[17.85,75.6,2,7,17],[17.02,67.06,2,8,17],[17.84,58.55,2,9,17],[20.01,50.29,2,10,17],[23.65,42.49,2,11,17],[28.52,35.39,2,12,17],[74.65,27.7,2,13,17],[82.54,30.31,2,14,17],[89.82,34.49,2,15,17],[96.12,40.07,2,16,17],[104.09,98.69,3,0,18],[98.06,104.71,3,1,18],[90.96,109.63,3,2,18],[83.22,113.28,3,3,18],[74.92,115.44,3,4,18],[66.37,116.22,3,5,18],[57.83,115.44,3,6,18],[49.52,113.32,3,7,18],[41.72,109.68,3,8,18],[34.62,29.34,3,9,18],[41.66,24.39,3,10,18],[49.41,20.76,3,11,18],[57.7,18.58,3,12,18],[66.31,17.8,3,13,18],[74.76,18.56,3,14,18],[83.07,20.75,3,15,18],[90.82,24.35,3,16,18],[97.95,29.29,3,17,18],[47.93,122.09,4,0,18],[39.99,118.74,4,1,18],[32.65,114.32,4,2,18],[26.06,108.77,4,3,18],[20.34,102.37,4,4,18],[15.62,95.15,4,5,18],[12.01,87.33,4,6,18],[9.61,79.19,4,7,18],[8.37,70.64,4,8,18],[8.57,62.02,4,9,18],[9.88,53.5,4,10,18],[12.51,45.3,4,11,18],[16.37,37.65,4,12,18],[21.15,30.56,4,13,18],[27.04,24.32,4,14,18],[33.75,18.96,4,15,18],[41.34,14.68,4,16,18],[103.91,35.34,4,17,18],[110.48,104.61,5,0,17],[104.38,110.73,5,1,17],[97.55,115.93,5,2,17],[89.99,120.04,5,3,17],[81.94,122.92,5,4,17],[73.43,124.68,5,5,17],[64.85,125.0,5,6,17],[56.24,124.2,5,7,17],[49.25,11.58,5,8,17],[57.67,9.67,5,9,17],[66.33,9.0,5,10,17],[75.26,9.73,5,11,17],[83.58,11.62,5,12,17],[91.58,14.88,5,13,17],[99.14,19.14,5,14,17],[105.79,24.46,5,15,17],[111.63,30.8,5,16,17]];

function svgEl(name, attrs = {}) {
  const el = document.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value);
  }
  return el;
}

function clamp(v, a = 0, b = 1) {
  return Math.max(a, Math.min(b, v));
}

function easeInOut(x) {
  x = clamp(x);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

function lerp(a, b, u) {
  return a + (b - a) * u;
}

function buildLeadIndices() {
  const angleOrder = C_DOTS.map((d, i) => ({
    i,
    a: (Math.atan2(d[1] - 67, d[0] - 60) + TAU) % TAU,
  })).sort((a, b) => a.a - b.a);

  const leadIndices = new Map();
  for (let slot = 0; slot < SPINNER_SLOTS; slot++) {
    const pick = angleOrder[Math.min(
      angleOrder.length - 1,
      Math.round(slot * (angleOrder.length - 1) / (SPINNER_SLOTS - 1))
    )];
    leadIndices.set(pick.i, slot);
  }
  return leadIndices;
}

const LEAD_INDICES = buildLeadIndices();

function orbitOpacity(particle) {
  if (!particle.lead) {
    return 0;
  }
  const q = (particle.slot + 1) / SPINNER_SLOTS;
  return 0.06 + 0.94 * q * q;
}

function createInstance(svg) {
  const particles = C_DOTS.map((d, i) => {
    const targetAngle = (Math.atan2(d[1] - 67, d[0] - 60) + TAU) % TAU;
    const isLead = LEAD_INDICES.has(i);
    const slot = isLead
      ? LEAD_INDICES.get(i)
      : Math.round(targetAngle / TAU * SPINNER_SLOTS) % SPINNER_SLOTS;
    const circle = svgEl('circle', {
      cx: 60,
      cy: 67,
      r: 2.8,
      fill: FILL_COLOR,
      opacity: 0,
      class: 'spinner-morph-c__dot',
    });
    svg.appendChild(circle);
    return {
      el: circle,
      tx: d[0],
      ty: d[1],
      slot,
      lead: isLead,
    };
  });

  return { svg, particles };
}

function updateFrame(instance, t) {
  const D = 5.75;
  const p = t % D;
  const omega = TAU / 1.15;
  const cx = 60;
  const cy = 67;
  let morph = 0;
  let orbitRot = p * omega;

  if (p < 1.6) {
    morph = 0;
  } else if (p < 2.4) {
    morph = easeInOut((p - 1.6) / 0.8);
    orbitRot = 1.6 * omega;
  } else if (p < 3.8) {
    morph = 1;
    orbitRot = 1.6 * omega;
  } else if (p < 4.6) {
    morph = 1 - easeInOut((p - 3.8) / 0.8);
    orbitRot = p * omega;
  } else {
    morph = 0;
  }

  const holdPulse = p >= 2.4 && p < 3.8
    ? Math.sin(((p - 2.4) / 1.4) * Math.PI)
    : 0;
  const stampScale = 1 + holdPulse * 0.018;
  instance.svg.style.transform = `scale(${stampScale.toFixed(4)})`;

  for (const d of instance.particles) {
    const radius = 28.5;
    const angle = (d.slot / SPINNER_SLOTS) * TAU + orbitRot - Math.PI / 2;
    const orbitX = cx + Math.cos(angle) * radius;
    const orbitY = cy + Math.sin(angle) * radius;
    const x = lerp(orbitX, d.tx, morph);
    const y = lerp(orbitY, d.ty, morph);
    const opacity = lerp(orbitOpacity(d), 1, morph);
    const startRadius = d.lead ? 3.45 : 1.6;
    const dotRadius = lerp(startRadius, 3.12, morph) * (1 + holdPulse * 0.035);

    d.el.setAttribute('cx', x.toFixed(2));
    d.el.setAttribute('cy', y.toFixed(2));
    d.el.setAttribute('r', dotRadius.toFixed(2));
    d.el.setAttribute('opacity', opacity.toFixed(3));
  }
}

export default {
  data() {
    return {
      animationFrameId: null,
      destroyed: false,
      instance: null,
      startTime: null,
    };
  },
  mounted() {
    this.instance = createInstance(this.$refs.svg);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      updateFrame(this.instance, 3.0);
      return;
    }

    this.startTime = performance.now();
    this.animationFrameId = requestAnimationFrame(this.tick);
  },
  beforeDestroy() {
    this.destroyed = true;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  },
  methods: {
    tick(now) {
      if (this.destroyed) {
        return;
      }

      const elapsed = (now - this.startTime) / 1000;
      updateFrame(this.instance, elapsed);
      this.animationFrameId = requestAnimationFrame(this.tick);
    },
  },
};
</script>

<style lang="scss" scoped>
.spinner-morph-c {
  display: block;
  width: 92px;
  height: 103px;
  overflow: visible;
}
</style>
